import { getGaugeTooltipMetrics } from '../Chart/utils/build-gauge-v2-tooltip';
import { getHandlebarsFields } from '../DataProvider/utils/handlebars-field-config';
import { extractMetricsWithChartTypes } from '../DataProvider/utils/text-metrics';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import {
  type CompositionV4Metric,
  isCompositionV4Metric,
  type MetricValues,
} from '../PageConfigProvider/types/metric';

const isDemandSupplyMetric = (
  metric: MetricValues
): metric is 'demand_supply' | 'demand_supply_percentile' =>
  metric === 'demand_supply' || metric === 'demand_supply_percentile';

const isWagePressureMetric = (
  metric: MetricValues
): metric is 'wage_pressure' | 'wage_pressure_percentile' =>
  metric === 'wage_pressure' || metric === 'wage_pressure_percentile';

export const isComputedFromComposition = (metric: MetricValues): boolean =>
  isDemandSupplyMetric(metric) ||
  isWagePressureMetric(metric) ||
  metric === 'market_tightness';

type CompositionInputs = {
  metrics: CompositionV4Metric[];
  overTime: boolean;
};

export const compositionInputsOf = (
  metric: MetricValues
): CompositionInputs | undefined => {
  if (isDemandSupplyMetric(metric)) {
    return { metrics: ['headcount'], overTime: false };
  }
  if (isWagePressureMetric(metric)) {
    return { metrics: ['salary'], overTime: true };
  }
  if (metric === 'market_tightness') {
    return { metrics: ['headcount', 'salary'], overTime: true };
  }
  return isCompositionV4Metric(metric)
    ? { metrics: [metric], overTime: false }
    : undefined;
};

export const getHandlebarsCompositionMetrics = (
  item: PositionedItem
): MetricValues[] => {
  const chartType = item.plot?.chartType;
  const dimensionSource = item.plot?.dimensionSource ?? 'primary';
  if (
    (chartType !== 'gauge-v2' && chartType !== 'text') ||
    item.plot?.secondaryDimension ||
    dimensionSource !== 'primary'
  ) {
    return [];
  }
  const { metrics } = extractMetricsWithChartTypes(
    getHandlebarsFields(item, chartType)
  );
  const tooltipMetrics =
    chartType === 'gauge-v2'
      ? getGaugeTooltipMetrics(item.plot?.gaugeV2?.value ?? '')
      : [];
  return [...new Set([...metrics, ...tooltipMetrics])].filter(
    (metric) => compositionInputsOf(metric) !== undefined
  );
};
